/**
 * @module lib/logger
 * Lightweight client logger used across the customer frontend.
 * Exposes `debug`, `info`, `warn`, and `error` with ISO timestamps.
 */

const LEVELS = { debug: 10, info: 20, warn: 30, error: 40 };
const env = (process.env.REACT_APP_LOG_LEVEL || "info").toLowerCase();
const CURRENT_LEVEL = LEVELS[env] || LEVELS.info;

/**
 * @returns {string} ISO timestamp.
 */
function ts() {
  return new Date().toISOString();
}

/**
 * @param {string} level
 * @param {IArguments|any[]} args
 * @returns {any[]}
 */
function format(level, args) {
  // If first arg is a string it's treated as a context/tag; if second arg is a string it's the function name.
  let tag = level.toUpperCase();
  let rest = args;

  if (args.length > 0 && typeof args[0] === "string") {
    const context = args[0];
    if (args.length > 1 && typeof args[1] === "string") {
      const fn = args[1];
      tag = `${tag}::${context}::${fn}`;
      rest = args.slice(2);
    } else {
      tag = `${tag}::${context}`;
      rest = args.slice(1);
    }
  }

  const prefix = `[${ts()}] [${tag}]`;
  return [prefix, ...rest];
}

/** @type {{ debug: (...args: any[]) => void, info: (...args: any[]) => void, warn: (...args: any[]) => void, error: (...args: any[]) => void }} */
const logger = {
  /** @param {...any} args */
  debug: (...args) => {
    if (CURRENT_LEVEL <= LEVELS.debug) console.debug(...format("debug", args));
  },
  /** @param {...any} args */
  info: (...args) => {
    if (CURRENT_LEVEL <= LEVELS.info) console.info(...format("info", args));
  },
  /** @param {...any} args */
  warn: (...args) => {
    if (CURRENT_LEVEL <= LEVELS.warn) console.warn(...format("warn", args));
  },
  /** @param {...any} args */
  error: (...args) => {
    if (CURRENT_LEVEL <= LEVELS.error) console.error(...format("error", args));
  },
};

export default logger;
